import * as CONST from "../../../app/constants"
import {
    CHANGE_REQUEST_STATUS_SUBMITTED,
    CHANGE_REQUEST_TYPE_MAIN_AGENT
} from "../../../app/constants"
import {
    combineAddress,
    decideLegalAndOperatingNames,
    fullName,
    humanReadableDate
} from "../../../app/utils"
import {
    applicationTypeFromLicenceSubType,
    rjscOfficerObjFromEntityId
} from "../../slice/applicationsUtils"
import {
    INTERNAL_ID_PREFIX,
    LICENCED_EXEMPT,
    LICENCED_INDIVIDUAL,
    LICENCED_UNDER_BROKERAGE,
    NOT_LICENCED
} from "../../slice/primaryAgencyApplicationSlice"
import { complainceOfficerDutiesList, principalBrokerDutiesList } from "../../slice/principalBrokerDutiesUtil"

export function parseConfirmationInformationForRenewal(applicationItem) {
    const { isMRACompliance } = applicationItem

    return [{
        duty: "Are you in compliance with the Mortgage Act and regulations?",
        confirmation: isMRACompliance ? "Yes" : "No",
        review: null
    }]
}

export function parseConfirmationInformation(applicationItem) {

    const { principalBrokerDutiesConfirmation } = applicationItem

    return principalBrokerDutiesList.map(duty => ({
        duty,
        confirmation: principalBrokerDutiesConfirmation ? "Yes" : "No",
        review: ""
    }))
}

export function parseComplianceOfficerConfirmationInformation(applicationItem) {

    const { principalBrokerDutiesConfirmation, personData } = applicationItem

    const agentFullName = fullName(personData)
    return complainceOfficerDutiesList.map(duty => ({
        duty: duty.replace("#AGENT_FULL_NAME#", agentFullName),
        confirmation: principalBrokerDutiesConfirmation ? "Yes" : "No",
        review: ""
    }))
}

export function parseMortgageApplicantInformationForRenewal(applicationItem, archiveApplicationItem) {
    /**
        parameterName : key,
        changeFrom : fromElement,
        changeTo : toElement,
        review : (fromElement === toElement) ? "complete" : null,
     */
    let applicantInformation = []

    const {
        companyRjscDetails: companyRjscDetailsTo
    } = applicationItem

    const {
        companyRjscDetails: companyRjscDetailsFrom
    } = archiveApplicationItem

    const registryIdFrom = companyRjscDetailsFrom.businessEntity?.registryNumber
    const registryIdTo = companyRjscDetailsTo.businessEntity?.registryNumber
    applicantInformation.push(
        {
            parameterName: "registryId",
            changeFrom: registryIdFrom,
            changeTo: registryIdTo,
            review: (registryIdTo === registryIdFrom) ? "complete" : null,
        }
    )

    const businessNamesFrom = decideLegalAndOperatingNames(companyRjscDetailsFrom)
    const businessNamesTo = decideLegalAndOperatingNames(companyRjscDetailsTo)

    applicantInformation.push(
        {
            parameterName: "legalName",
            changeFrom: businessNamesFrom.legalName,
            changeTo: businessNamesTo.legalName,
            review: (businessNamesFrom.legalName === businessNamesTo.legalName) ? "complete" : null,
        }
    )

    applicantInformation.push(
        {
            parameterName: "operatingName",
            changeFrom: businessNamesFrom.operatingName,
            changeTo: businessNamesTo.operatingName,
            review: (businessNamesFrom.operatingName === businessNamesTo.operatingName) ? "complete" : null,
        }
    )

    const companyTrademarkFrom = archiveApplicationItem?.companyTrademark ?? ""
    const companyTrademarkTo = applicationItem?.companyTrademark ?? ""

    applicantInformation.push(
        {
            parameterName: "tradeMark",
            changeFrom: companyTrademarkFrom,
            changeTo: companyTrademarkTo,
            review: (companyTrademarkFrom === companyTrademarkTo) ? "complete" : null,
        }
    )

    const businessAddress = (rjscDetails) => {
        if (rjscDetails
            && rjscDetails.businessContact) {

            if (rjscDetails.businessContact.homeJurisdictionAddress)
                return combineAddress(rjscDetails.businessContact.homeJurisdictionAddress)
            else if (rjscDetails.businessContact.civicAddress)
                return combineAddress(rjscDetails.businessContact.civicAddress)
        }

        return ""
    }

    const rjscOfficial = (rjscDetails) => {
        const officialsWithRjsc = []
        if (rjscDetails
            && rjscDetails.businessData
            && rjscDetails.businessData.peopleList
            && Array.isArray(rjscDetails.businessData.peopleList)) {
            rjscDetails.businessData.peopleList.forEach(p => {
                officialsWithRjsc.push(fullName(p) + ", " + p.position)
            })
        }
        return officialsWithRjsc
    }

    const businessAddressOriginal = businessAddress(companyRjscDetailsFrom)
    const businessAddressChanged = businessAddress(companyRjscDetailsTo)

    applicantInformation.push(
        {
            parameterName: "businessAddress",
            changeFrom: businessAddressOriginal,
            changeTo: businessAddressChanged,
            review: (businessAddressOriginal === businessAddressChanged) ? "complete" : null,
        }
    )

    const originalRjscOfficers = rjscOfficial(companyRjscDetailsFrom).join("\n")
    const changedRjscOfficers = rjscOfficial(companyRjscDetailsTo).join("\n")

    applicantInformation.push(
        {
            parameterName: "rjscOfficials",
            changeFrom: originalRjscOfficers,
            changeTo: changedRjscOfficers,
            review: (originalRjscOfficers === changedRjscOfficers) ? "complete" : null,
        }
    )

    if (applicantInformation.every(obj => obj.review === "complete")) {
        applicantInformation[0].review = null
        applicantInformation[0].forceSelection = true
    }

    return applicantInformation
}

export function parseMortgageApplicantInformation(applicationItem) {
    let applicantInformation = []

    const {
        registryNumber,
        companyRjscDetails
    } = applicationItem

    if (companyRjscDetails?.businessData?.relatedTo) {
        applicantInformation.push(
            {
                "Registry ID": companyRjscDetails.businessData.relatedTo,
                review: "complete",
            }
        )
    }
    else {
        applicantInformation.push(
            {
                "Registry ID": registryNumber ? registryNumber : "",
                review: "complete",
            }
        )
    }


    if (companyRjscDetails?.businessData?.businessOwner) {
        applicantInformation.push(
            {
                "Legal name": companyRjscDetails.businessData.businessOwner,
                review: "complete",
            }
        )

        applicantInformation.push(
            {
                "Operating name": applicationItem.businessName ?? "",
                review: "complete",
            }
        )
    } else {
        applicantInformation.push(
            {
                "Legal name": applicationItem.businessName ?? "",
                review: "complete",
            }
        )

        applicantInformation.push(
            {
                "Operating name": "",
                review: "complete",
            }
        )
    }

    applicantInformation.push(
        {
            "Trademark": applicationItem.companyTrademark ?? "",
            review: "",
        }
    )

    let businessAddress = ""
    if (companyRjscDetails
        && companyRjscDetails.businessContact) {

        if (companyRjscDetails.businessContact.homeJurisdictionAddress)
            businessAddress = combineAddress(companyRjscDetails.businessContact.homeJurisdictionAddress)
        else if (companyRjscDetails.businessContact.civicAddress)
            businessAddress = combineAddress(companyRjscDetails.businessContact.civicAddress)
    }

    applicantInformation.push(
        {
            "Business Address": businessAddress,
            review: "complete",
        }
    )

    let officialsWithRjsc = []
    if (companyRjscDetails
        && companyRjscDetails.businessData
        && companyRjscDetails.businessData.peopleList
        && Array.isArray(companyRjscDetails.businessData.peopleList)) {
        companyRjscDetails.businessData.peopleList.forEach(p => {
            officialsWithRjsc.push(fullName(p) + ", " + p.position)
        })
    }

    applicantInformation.push(
        {
            "Officials with RJSC": officialsWithRjsc.join("\n"),
            review: "complete",
        }
    )

    return applicantInformation
}

export function parseMortgageBrokerInformation(applicationItem) {
    const { personData } = applicationItem

    const reviewData = []

    reviewData.push({
        "Home address": combineAddress(personData?.addressData),
        "review": ""
    })
    reviewData.push({
        "Phone": personData.phoneNumber,
        "review": ""
    })
    reviewData.push({
        "Alternate phone": personData.alternatePhoneNumber,
        "review": ""
    })
    reviewData.push({
        "Email": personData.email,
        "review": ""
    })

    return reviewData
}

/*
export function currentIndividualAgentLicenceInformation(applicationItem, documents) {
    const {
        mblraLicenceNumber,
        licenceHistoryData, 
        workHistoryData,
        mraNSLicenceNumber,
    } = applicationItem

    const workHistoryDocuments = documents.filter(d => d.documentType === CONST.DOCUMENT_TYPE_WORK_EXPERIENCE)
    
    const licenceHistoryDataElements = []

    licenceHistoryDataElements.push({
        "Was the applicant previously licensed as a Mortgage Broker in Nova Scotia?" : mraNSLicenceNumber ? "Yes" : "No",
        "review" : ""
    })

    if(mraNSLicenceNumber) { 
        licenceHistoryDataElements.push({
            "NS Licence Number" : mraNSLicenceNumber,
            "review" : ""
        }) 
    }

    if(licenceHistoryData) {
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?" : "Yes",
            "review" : ""
        })
        licenceHistoryDataElements.push({
            "Province" : licenceHistoryData.province,
            "review" : ""
        })
        licenceHistoryDataElements.push({
            "Licence number" : licenceHistoryData.licenceNumber,
            "review" : ""
        })
        licenceHistoryDataElements.push({
            "Expiry date" : humanReadableDate(licenceHistoryData.endDate),
            "review" : ""
        })
    } else { 
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?" : "No",
            "review" : ""
        })
    }

    if(mblraLicenceNumber) {
        licenceHistoryDataElements.push({
            "Licence number under MRA" : mblraLicenceNumber,
            "review" : ""
        })
    }
    

    if(workHistoryData && workHistoryData.length > 0) 
    {
        workHistoryData.forEach(workHistory => {
            licenceHistoryDataElements.push({
                "title" : "Work History",
                "Start Date" : humanReadableDate(workHistory.startDate),
                "Duration" : workHistory.duration,
                "Description" : workHistory.description,
                "review" : ""
            })
        })
    } else { 
        licenceHistoryDataElements.push({
            "Work history" : "N/A",
            "review" : ""
        })
    }

    if(workHistoryDocuments) { 
        workHistoryDocuments.forEach(doc => {
            licenceHistoryDataElements.push({
                ...doc,
                "document" : 1,
                "review" : ""
            })
        })
    }

    return licenceHistoryDataElements
}
*/


export function currentIndividualPrincipalBrokerInformation(applicationItem) {
    const {
        currentlyMBinBusiness,
        mraNSLicenceNumber,
    } = applicationItem

    const licenceHistoryDataElements = []

    licenceHistoryDataElements.push({
        "Current NS Licence Number": mraNSLicenceNumber,
        "review": ""
    })

    licenceHistoryDataElements.push({
        "Currently a mortgage broker on the business for which applied to be the new Principal Broker?": currentlyMBinBusiness ? "Yes" : "No",
        "review": ""
    })

    return licenceHistoryDataElements
}

export function currentMortgageBrokerLicenceInformation(applicationItem, documents) {
    const {
        previouslyLicencedStatus,
        mblraLicenceNumber,
        licenceHistoryData,
        workHistoryData,
        brokerLicenceNumber,
        currentlyBrokerInNS,
        licenceUpgradeApplicationId
    } = applicationItem

    const workHistoryDocuments = documents.filter(d => d.documentType === CONST.DOCUMENT_TYPE_WORK_EXPERIENCE && !d.isUpdated)

    const licenceHistoryDataElements = []

    if (previouslyLicencedStatus === LICENCED_INDIVIDUAL
        || previouslyLicencedStatus === LICENCED_UNDER_BROKERAGE) {
        licenceHistoryDataElements.push({
            "What type of licence are you applying for?": "Mortgage Broker Licence",
            "review": ""
        })
    } else if (previouslyLicencedStatus === NOT_LICENCED) {
        licenceHistoryDataElements.push({
            "What type of licence are you applying for?": "Associate Mortgage Broker Licence",
            "review": ""
        })
    }
    else if (previouslyLicencedStatus === LICENCED_EXEMPT) {
        licenceHistoryDataElements.push({
            "What type of licence are you applying for?": "Mortgage Broker Licence - The applicant brokered mortgages for a federally regulated financial institution for at least 24 of the 36 months immediately before the date of their application.",
            "review": ""
        })
    }

    licenceHistoryDataElements.push({
        "Was the applicant previously licensed as a Mortgage Broker in Nova Scotia?": currentlyBrokerInNS ? "Yes" : "No",
        "review": ""
    })

    if (currentlyBrokerInNS && !licenceUpgradeApplicationId) {
        licenceHistoryDataElements.push({
            "NS Licence Number": brokerLicenceNumber,
            "review": ""
        })
    }

    // To show associate broker upgade to mortgage broker
    if (licenceUpgradeApplicationId) {
        licenceHistoryDataElements.push({
            "Associate Mortgage Broker upgrade Mortgage Broker": "Yes",
            "review": ""
        })
    }

    if (licenceHistoryData) {
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?": "Yes",
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Province": licenceHistoryData.province,
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Licence number": licenceHistoryData.licenceNumber,
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Expiry date": humanReadableDate(licenceHistoryData.endDate),
            "review": ""
        })
    } else {
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?": "No",
            "review": ""
        })
    }

    if (mblraLicenceNumber) {
        licenceHistoryDataElements.push({
            "Licence number under MRA": mblraLicenceNumber,
            "review": ""
        })
    }


    if (workHistoryData && workHistoryData.length > 0) {
        workHistoryData.forEach(workHistory => {
            licenceHistoryDataElements.push({
                "title": "Work History",
                "Start Date": humanReadableDate(workHistory.startDate),
                "Duration": workHistory.duration,
                "Description": workHistory.description,
                "review": ""
            })
        })
    } else {
        licenceHistoryDataElements.push({
            "Work history": "N/A",
            "review": ""
        })
    }

    if (workHistoryDocuments) {
        workHistoryDocuments.forEach(doc => {
            licenceHistoryDataElements.push({
                ...doc,
                "document": 1,
                "review": ""
            })
        })
    }

    return licenceHistoryDataElements
}

export function currentMortgageBrokerLicenceInformationDeprecated(applicationItem, documents) {
    const {
        previouslyLicencedStatus,
        mblraLicenceNumber,
        licenceHistoryData,
        workHistoryData
    } = applicationItem

    const workHistoryDocuments = documents.filter(d => d.documentType === CONST.DOCUMENT_TYPE_WORK_EXPERIENCE && !d.isUpdated)

    const licenceHistoryDataElements = []
    const workHistoryDataElements = []
    const workHistoryElements = []

    if (previouslyLicencedStatus === LICENCED_INDIVIDUAL
        || previouslyLicencedStatus === LICENCED_UNDER_BROKERAGE) {
        licenceHistoryDataElements.push({
            "Was the applicant previously licensed as a mortgage broker in Nova Scotia?": "Yes",
            "review": ""
        })

        licenceHistoryDataElements.push({
            "Licence number under MBLRA": mblraLicenceNumber,
            "review": ""
        })
    } else {
        licenceHistoryDataElements.push({
            "Was the applicant previously licensed as a mortgage broker in Nova Scotia?": "No",
            "review": ""
        })
    }

    if (licenceHistoryData) {
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?": "Yes",
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Province": licenceHistoryData.province,
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Licence number": licenceHistoryData.licenceNumber,
            "review": ""
        })
        licenceHistoryDataElements.push({
            "Expiry date": humanReadableDate(licenceHistoryData.endDate),
            "review": ""
        })
    } else {
        licenceHistoryDataElements.push({
            "Does the applicant have a current licence in another province?": "No",
            "review": ""
        })
    }

    if (workHistoryData && workHistoryData.length > 0) {
        workHistoryData.forEach(workHistory => {
            workHistoryElements.push(
                <div className="bottomMarginMedium">
                    <div className="bottomMarginMedium">
                        <b>Start Date</b>
                        <br />
                        {humanReadableDate(workHistory.startDate)}
                    </div>
                    <div className="bottomMarginMedium">
                        <b>Duration</b>
                        <br />
                        {workHistory.duration}
                    </div>
                    <div className="bottomMarginMedium">
                        <b>Description</b>
                        <br />
                        {workHistory.description}
                    </div>
                </div>
            )
        })
    } else {
        workHistoryElements.push(<div className="bottomMarginMedium">N/A</div>)
    }

    workHistoryDataElements.push({
        "Work history": workHistoryElements,
        "review": ""
    })

    return [{
        licenceHistoryData: licenceHistoryDataElements,
        workHistoryData: workHistoryDataElements,
        workHistoryDocuments
    }]
}

function principalBrokerQualificiationsInformationForRenewal(applicationItem) {
    const { legalName, operatingName } = decideLegalAndOperatingNames(applicationItem?.businessLicenceData?.companyRjscDetails)

    const nameCombination = operatingName ? `${operatingName}(${legalName})` : `${legalName}`
    const confirmationQ1 = `Is the Mortgage Broker authorized to carry out the duties and responsibilities of a mortgage broker on behalf of ${nameCombination}`
    const confirmationQ2 = `Is the Mortgage Broker an officer/director for a corporation/limited partnership , a partner in a partnership, or a sole proprietor?  `
    const confirmationQ3 = `Are you still the Principal Broker for ${nameCombination}`

    return [{
        [confirmationQ1]: "Yes",
        [confirmationQ2]: "Yes",
        [confirmationQ3]: "Yes"
    }]
}

function mortgageBrokerQualificiationsInformationForRenewal(applicationItem) {
    const { legalName, operatingName } = decideLegalAndOperatingNames(applicationItem?.businessLicenceData?.companyRjscDetails)

    const confirmationQ1 = `Is the Mortgage Broker 19 years of age or older?`
    const confirmationQ2 = `Is the Mortgage Broker a resident of Canada?`
    const confirmationQ3 = `Is the Mortgage Broker authorized to carry out ` +
        `the duties and responsibilities of a mortgage broker ` +
        `on behalf of ${legalName}${operatingName ? `(${operatingName})` : ''}.`

    return [{
        [confirmationQ1]: "Yes",
        [confirmationQ2]: "Yes",
        [confirmationQ3]: "Yes"
    }]
}

function associateMortgageBrokerQualificiationsInformationForRenewal(applicationItem) {
    const { legalName, operatingName } = decideLegalAndOperatingNames(applicationItem?.businessLicenceData?.companyRjscDetails)

    const confirmationQ1 = `Is the Associate Mortgage Broker 19 years of age or older?`
    const confirmationQ2 = `Is the Associate Mortgage Broker a resident of Canada?`
    const confirmationQ3 = `Is the Associate Mortgage Broker authorized to carry out`
        + ` the duties and responsibilities of a mortgage broker ` +
        `on behalf of ${legalName}${operatingName ? `(${operatingName})` : ''}.`

    return [{
        [confirmationQ1]: "Yes",
        [confirmationQ2]: "Yes",
        [confirmationQ3]: "Yes"
    }]
}

export function brokerQualificiationsInformationForRenewal(applicationItem) {
    if (applicationItem.licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION) {
        return principalBrokerQualificiationsInformationForRenewal(applicationItem)
    } else if (applicationItem.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
        return mortgageBrokerQualificiationsInformationForRenewal(applicationItem)
    } else if (applicationItem.licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
        return associateMortgageBrokerQualificiationsInformationForRenewal(applicationItem)
    }

    return [{}]
}

export function brokerEducationRequirementInformation(applicationItem, documents, cepRipExpiryDate) {
    const {
        previouslyLicencedStatus,
        licenceAnotherProvince,
        hasRequiredEducationQualification,
        hasRequiredEducationCertificate,
        confirmEducationQualificationCompletion,
        confirmRequiredCourseCompletion
        
    } = applicationItem

    let educationCertificates = []
    const reviewData = []

    const brokerCourseCertificates = documents?.filter(d => d.documentType === CONST.DOCUMENT_TYPE_BROKER_COURSE_CERTIFICATE)

    if (previouslyLicencedStatus === LICENCED_EXEMPT) {
        reviewData.push({
            "The applicant must complete the Nova Scotia Mortgage Broker Course and provide proof of completion before they can apply for a licence.":
                confirmRequiredCourseCompletion ? "Yes" : "No",
            "review": ""
        })

        brokerCourseCertificates.forEach(doc => {
            reviewData.push({
                ...doc,
                "document": 1,
                "review": null
            })
        })
    } else {
        if (licenceAnotherProvince) {
            if (previouslyLicencedStatus === NOT_LICENCED) {
                reviewData.push({
                    "Has the applicant completed the education requirement?":
                        "This applicant is exempt because they have a current Associate Mortgage Broker Licence (or equivalent) from an exempt province.",
                    "review": ""
                })
            }
            else {
                reviewData.push({
                    "Has the applicant completed the education requirement?":
                        "This applicant is exempt because they have a current Mortgage Broker Licence from an exempt province.",
                    "review": ""
                })
            }
            
        } else {
            reviewData.push({
                "Has the applicant completed the education requirement?": hasRequiredEducationQualification ? "Yes" : "No",
                "review": ""
            })

            if (!hasRequiredEducationCertificate && confirmEducationQualificationCompletion) {
                reviewData.push({
                    "Certificate of completion":
                        "The applicant agrees to complete the course and the Principal broker will send the certificate of completion on their behalf within 1 year.",
                    "review": ""
                })
            } else {
                educationCertificates = documents.filter(d => d.documentType === CONST.DOCUMENT_TYPE_EDUCATION_CERTIFICATE && !d.isUpdated)
            }
         
            if(cepRipExpiryDate) {
                reviewData.push({
                    "MPC Continuing Education Program valid until date": humanReadableDate(cepRipExpiryDate),
                    "review": ""
                })
            }
        }

        educationCertificates.forEach(doc => {
            reviewData.push({
                ...doc,
                "document": 1,
                "review": null
            })
        })

        if (brokerCourseCertificates?.length) {
            brokerCourseCertificates.forEach(doc => {
                reviewData.push({
                    ...doc,
                    "document": 1,
                    "review": doc.review || null
                })
            })
        }
    }

    return reviewData
}

export function brokerCriminalRecordCheckInformationDeprecated(applicationItem, documents) {

    const { licencingSubType } = applicationItem

    if (licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION) {
        return [{
            criminalRecordsData: [{
                "Uploaded document": "already uploaded with the Mortgage brokerage application",
                "review": ""
            }],
            criminalRecordDocuments: []
        }]
    }

    const criminalRecords = documents.filter(d =>
        (d.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD && !d.isUpdated))

    return [{
        criminalRecordsData: [],
        criminalRecordDocuments: criminalRecords.map(d => ({ ...d, "review": "" }))
    }]
}

export function brokerConfirmationInformationForRenewal(brokerApplication, primaryApplication) {
    const {
        licencingSubType,
        // businessName,
        businessLicenceData
    } = brokerApplication

    const {
        personData
    } = businessLicenceData

    const principalBrokerPersonData = primaryApplication?.businessLicenceData?.personData
    const brokerName = fullName(personData)
    const pbName = fullName(principalBrokerPersonData)

    const reviewItems = []
    let confirmation1, confirmation2

    if (licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION) {
        confirmation1 = `I, <b>${brokerName}</b>, have read and understand the Mortgage Regulation Act and its regulations and agree to be bound by its provisions.`
        confirmation2 = `I, <b>${brokerName}</b>, have been in compliance with the Act and its Regulations.`
    } else if (licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION
        || licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
        confirmation1 = `I, <b>${pbName}</b>, confirm that, <b>${brokerName}</b> has read and understands the Mortgage Regulation Act and its regulations and agrees to be bound by its provisions.`
        confirmation2 = `I, <b>${pbName}</b>, confirm that, <b>${brokerName}</b> has been in compliance with the Act and its Regulations.`
    }

    reviewItems.push({
        duty: confirmation1,
        confirmation: "Yes",
        "review": null
    }, {
        duty: confirmation2,
        confirmation: "Yes",
        "review": null
    })

    return reviewItems
}

export function brokerEducationContinuationRequirementInformationForRenewal(brokerApplication) {
    const lastIssuedDate = brokerApplication.issuedDate;
    const applicationItem = brokerApplication.businessLicenceData
    const cepRipExpiryDate = brokerApplication.cepRipExpiryDate;

    const {
        mraBrokerRegulatoryInfoDate,
        mraBrokerMpcCompletionDate
    } = applicationItem;
    const { licencingSubType, documents } = brokerApplication
    const isAMBApplication = (applicationTypeFromLicenceSubType(licencingSubType) === CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE)
    const educationContinuingCertificates = documents?.filter(doc =>
        (doc.documentType === CONST.DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE && doc.changeType !== "DELETE") && (doc.createdDate > lastIssuedDate))
        .sort((f1, f2) => f2.createdDate - f1.createdDate)

    let ret = [
        { "MPC Continuing Education Program completion date": mraBrokerMpcCompletionDate ? humanReadableDate(mraBrokerMpcCompletionDate) : "---" }
    ];

    if (cepRipExpiryDate) {
        ret.push({
            "MPC Continuing Education Program valid until date": humanReadableDate(cepRipExpiryDate),
            review: ""
        });
    }

    if (!isAMBApplication) {
        ret.push({
            "Regulatory Information Program completion date": mraBrokerRegulatoryInfoDate
                ? humanReadableDate(mraBrokerRegulatoryInfoDate)
                : "---"
        });

        if (cepRipExpiryDate) {
            ret.push({
                "Regulatory Information Program valid until date": humanReadableDate(
                    cepRipExpiryDate
                ),
                review: ""
            });
        }
    }

    if (educationContinuingCertificates?.length > 0) {
        return [...ret, {
            ...educationContinuingCertificates[0],
            "document": true,
            "review": null
        }]
    }

    return [...ret, { "Education continuing certificate": "No Education continuing certificate uploaded." }]
}

export function brokerEducationRequirementInformationForRenewal(brokerApplication, documents) {
    const educationCertificates = documents?.filter(doc =>
        doc.documentType === CONST.DOCUMENT_TYPE_EDUCATION_CERTIFICATE && doc.changeType !== "DELETE")
        .sort((doc1, doc2) => (doc1.createdDate < doc2.createdDate ? -1 : 1))

    const educationReviewData = []

    if (educationCertificates?.length) {
        const latestEducationCert = educationCertificates[0]

        educationReviewData.push({
            "Submission date": humanReadableDate(latestEducationCert.createdDate),
        }, {
            ...latestEducationCert,
            "document": true,
            "review": null
        })
    }

    const brokerCourseCertificates = documents?.filter(d => d.documentType === CONST.DOCUMENT_TYPE_BROKER_COURSE_CERTIFICATE)
    if (brokerCourseCertificates?.length) {
        brokerCourseCertificates.forEach(doc => {
            educationReviewData.push({
                ...doc,
                "document": 1,
                "review": doc.review || null
            })
        })
    }

    if (educationReviewData.length) {
        return educationReviewData
    }

    return null

    // return [{
    //     "Certificate of completion": "No file uploaded",
    //     "review": null
    // }]
}

export function brokerCriminalRecordCheckInformationForRenewal(brokerApplication, documents, applicationArchive) {
    const { mraRenewalCrcNoChangeIds, mraRenewalCrcUpdatedIds } = brokerApplication?.businessLicenceData
    const lastIssuedDate = applicationArchive?.issuedDate ?? 0

    const crcNoChangeIds = mraRenewalCrcNoChangeIds?.split(',')
    const crcUpdatedIds = mraRenewalCrcUpdatedIds?.split(',')

    let reviewItem = {}

    const brokerId = INTERNAL_ID_PREFIX + '-broker'
    const findDocumentForBroker = entityId => entityId === brokerId
    const question = "I confirm there have not been any changes to the previous criminal record check I submitted to the Registrar."

    const latestCrcUpdateDocuments = documents.filter(doc =>
    (doc.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD 
        && doc.changeType !== "DELETE"
        && doc.createdDate > lastIssuedDate 
        && !doc.isUpdated))
        .sort((doc1, doc2) => (doc1.createdDate < doc2.createdDate ? -1 : 1))

    if (crcNoChangeIds?.length && crcNoChangeIds.find(findDocumentForBroker)) {
        // no change
        reviewItem = { [question]: "Yes" }
    } else if (crcUpdatedIds?.length > 0 && crcUpdatedIds.find(findDocumentForBroker)) {
        // file uploaded
        reviewItem = { [question]: "No" }

        // Object.defineProperty(reviewItem, "Uploaded Document", {
        //     value: latestCrcUpdateDocuments?.length ? latestCrcUpdateDocuments[0] : "NA",
        //     writable: true, 
        //     enumerable: true
        // })

        if (latestCrcUpdateDocuments?.length) {
            return [reviewItem, {
                ...latestCrcUpdateDocuments[0],
                "document": true,
                "review": null
            }]
        }
    }

    return [reviewItem]
}

export function brokerCriminalRecordCheckInformation(licencingSubType, applicationItem, documents) {
    const criminalRecordsData = []

    if (licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION) {
        criminalRecordsData.push({
            "Uploaded document": "Document is already attached with the Mortgage Brokerage application",
            "review": ""
        })
    } else {
        const criminalRecords = documents.filter(d => d.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD && !d.isUpdated)

        criminalRecords.forEach(d => {
            criminalRecordsData.push({
                ...d,
                "document": 1,
                "review": null
            })
        })
    }

    return criminalRecordsData
}

export function brokerRegulatoryInformation(applicationItem, cepRipExpiryDate) {
    const { confirmSuretyBond } = applicationItem


    return [
        {
            "Received survey submission?": "",
            review: ""
        },
        {
            "Date received survey submission": confirmSuretyBond ?? 0,
            review: confirmSuretyBond ? "complete" : null
        },
        {
            "Regulatory Information Program valid until date": cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---",
            "review": ""
        }
    ];
}

export function brokerConfirmationInformation(applicationItem, primaryApplication) {
    const {
        licencingSubType,
        personData,
        businessName,
    } = applicationItem

    const principalBrokerPersonData = primaryApplication?.businessLicenceData?.personData

    let confirmationText = ""
    confirmationText = `As a Principal Broker for <b>${businessName}</b>, I, <b>${fullName(principalBrokerPersonData)}</b>, confirm, that <b>${fullName(personData)}</b> has read and understood the Mortgage Regulation Act and its regulations and agrees to be bound by their provisions.`

    if (licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION) {
        // do we need separate text?        
    } else if (licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION
        || licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION) {

    }

    return [{
        duty: confirmationText,
        confirmation: "Yes",
        review: ""
    }]
}

export function parsePrincipalBrokerInformationForRenewal(applicationItem, archiveApplicationItem) {
    const information = (appData) => {
        const { personData } = appData

        return {
            name: fullName(personData) ?? "",
            homeAddress: combineAddress(personData?.addressData) ?? "",
            phone: personData.phoneNumber ?? "",
            alternatePhone: personData.alternatePhoneNumber ?? "",
            email: personData.email ?? ""
        }
    }

    const mergeKeys = (obj1, obj2) => {
        const obj1Keys = Object.keys(obj1)
        const obj2Keys = Object.keys(obj2)

        const mergedKeys = []

        for (let index = 0; index < Math.max(obj1Keys.length, obj2Keys.length); index++) {

            if (index < obj1Keys.length && (mergedKeys.indexOf(obj1Keys[index]) === -1)) {
                mergedKeys.push(obj1Keys[index])
            }

            if (index < obj2Keys.length && (mergedKeys.indexOf(obj2Keys[index]) === -1)) {
                mergedKeys.push(obj2Keys[index])
            }
        }

        return mergedKeys
    }

    const changeFromObj = archiveApplicationItem ? information(archiveApplicationItem) : {}
    const changeToObj = information(applicationItem)

    let pbInformation = []
    const parameters = mergeKeys(changeFromObj, changeToObj)

    parameters.forEach(key => {
        let fromElement = ""
        let toElement = ""

        if (Object.hasOwnProperty.call(changeFromObj, key)) {
            fromElement = changeFromObj[key];
        }

        if (Object.hasOwnProperty.call(changeToObj, key)) {
            toElement = changeToObj[key]
        }

        pbInformation.push({
            parameterName: key,
            changeFrom: fromElement,
            changeTo: toElement,
            forceSelection: true,
            review: "",
        })
    })

    // if (pbInformation.every(obj => obj.review === "complete")) {
    //     pbInformation[0].review = null
    //     pbInformation[0].forceSelection = true
    // }

    return pbInformation
}

export function parsePrincipalBrokerInformation(applicationItem) {
    const { personData } = applicationItem

    const reviewData = []

    reviewData.push({
        "Name": fullName(personData),
        "review": ""
    })
    reviewData.push({
        "Home address": combineAddress(personData?.addressData),
        "review": ""
    })
    reviewData.push({
        "Phone": personData.phoneNumber,
        "review": ""
    })

    reviewData.push({
        "Alternate phone": personData.alternatePhoneNumber,
        "review": ""
    })

    reviewData.push({
        "Email": personData.email,
        "review": ""
    })

    return reviewData
}

export function parseCancelLicenceInformationMap (applicationItem, application) {
     const { personData } = applicationItem

    const reviewData = []

    reviewData.push({
        "Name": fullName(personData),
        "review": ""
    })
    reviewData.push({
        "Home address": combineAddress(personData?.addressData),
        "review": ""
    })
    reviewData.push({
        "Phone": personData.phoneNumber,
        "review": ""
    })

    reviewData.push({
        "Alternate phone": personData.alternatePhoneNumber,
        "review": ""
    })

    reviewData.push({
        "Email": personData.email,
        "review": ""
    })
    
    reviewData.push({
        "Licenece Number": application?.licenceNumber,
        "review": ""
    })

    reviewData.push({
       "Submitted Date": humanReadableDate(applicationItem?.cancelSubmittedDate),
        "review": ""
    })

    return reviewData;
}

export function parseFinancialRecordsInformationForRenewal(applicationItem, archiveApplicationItem) {

    const changeFrom = combineAddress(archiveApplicationItem.financialRecordsLocation)
    const changeTo = combineAddress(applicationItem.financialRecordsLocation)

    return [{
        parameterName: "civicAddress",
        changeFrom,
        changeTo,
        review: null,
        forceSelection: true
    }]
}

export function parseFinancialRecordsInformation(applicationItem) {
    const { financialRecordsLocation } = applicationItem

    return [{
        "Civic address": combineAddress(financialRecordsLocation),
        "review": ""
    }]
}

export function parseFiscalYearInformationForRenewal(applicationItem, archiveApplicationItem) {
    const changeFromDateObj = new Date(parseInt(archiveApplicationItem.companyFiscalEndDate))
    const changeToDateObj = new Date(parseInt(applicationItem.companyFiscalEndDate))

    const option = {
        month: "long",
        day: "numeric"
    }

    return (
        [{
            parameterName: "Date",
            changeFrom: changeFromDateObj.toLocaleDateString("en-US", option),
            changeTo: changeToDateObj.toLocaleDateString("en-US", option),
            review: null,
            forceSelection: true
        }]
    )
}

export function parseFiscalYearInformation(applicationItem) {
    const { companyFiscalEndDate } = applicationItem

    let dateObj = new Date(parseInt(companyFiscalEndDate))

    const option = {
        month: "long",
        day: "numeric"
    }

    return (
        [{
            "Date": dateObj.toLocaleDateString("en-US", option),
            "review": ""
        }]
    )
}

export function parseAppointedAgentInformationForRenewal(applicationItem, archiveApplicationItem) {
    const changeFrom = fullName(archiveApplicationItem.responsibleAgent)
    const changeTo = fullName(applicationItem.responsibleAgent)

    return [{
        parameterName: "name",
        changeFrom,
        changeTo,
        review: null,
        forceSelection: true
    }]
}

export function parseAppointedAgentInformation(applicationItem) {
    const { responsibleAgent } = applicationItem

    return [{
        "Name": fullName(responsibleAgent),
        "review": ""
    }]
}

export function parseServiceAddressInformationForRenewal(applicationItem, archiveApplicationItem) {
    const changeFrom = combineAddress(archiveApplicationItem.serviceBusinessAddress)
    const changeTo = combineAddress(applicationItem.serviceBusinessAddress)

    return [{
        parameterName: "civicAddress",
        changeFrom,
        changeTo,
        review: null,
        forceSelection: true
    }]
}

export function parseServiceAddressInformation(applicationItem) {
    const { serviceBusinessAddress } = applicationItem
    return [{
        "Civic address": combineAddress(serviceBusinessAddress),
        "review": ""
    }]
}

// TODO : add logic for the uploadedForRenewal flag. 
export function parseCriminalRecordsCheckInformationForRenewal(applicationItem, documents, applicationArchive) {
    const {
        mraRenewalCrcNoChangeIds,
        mraRenewalCrcUpdatedIds,
        companyRjscDetails
    } = applicationItem

    let allEntitiesWithCRC = [];
    if (mraRenewalCrcNoChangeIds) {
        allEntitiesWithCRC = mraRenewalCrcNoChangeIds.split(',');
    }
    if (mraRenewalCrcUpdatedIds) {
        allEntitiesWithCRC = allEntitiesWithCRC.concat(mraRenewalCrcUpdatedIds.split(','))
    }

    const lastIssuedDate = applicationArchive?.issuedDate ?? 0
    const peopleList = companyRjscDetails.businessData?.peopleList

    const criminalRecords = documents.filter((doc) => {
        if (doc.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD && doc.entityId && !doc.isUpdated) {
            if (!allEntitiesWithCRC.includes(doc.entityId.toString())) {
                return false;
            }
        }

        return doc.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD && doc.createdDate > lastIssuedDate
    });

    const noChangeCrcIds = mraRenewalCrcNoChangeIds.split(',')

    const reviewItems = noChangeCrcIds.filter(id => (id !== "")).map(id => {
        const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, id)
        if (rjscOfficer) {
            return {
                "Name": fullName(rjscOfficer),
                "Position": rjscOfficer.position,
                "review": ""
            }
        } else {
            return {
                "Name": id,
                "Position": "NA",
                "review": ""
            }
        }
    })

    const reviewItems1 = criminalRecords.length ?
        criminalRecords.map(record => {
            const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, record.entityId)
            if (rjscOfficer) {
                return {
                    ...record,
                    "Name": fullName(rjscOfficer),
                    "Position": rjscOfficer.position,
                    "uploadedForRenewal": true,
                    "review": ""
                }
            } else {
                return {
                    ...record,
                    "Name": record.entityId,
                    "Position": "NA",
                    "uploadedForRenewal": true,
                    "review": ""
                }
            }
        }) :
        []

    return [
        ...reviewItems,
        ...reviewItems1
    ]
}

export function parseCriminalRecordsCheckInformation(applicationItem, documents) {
    const { companyRjscDetails } = applicationItem
    const peopleList = companyRjscDetails?.businessData?.peopleList ?? []
    const criminalRecords = documents.filter(doc => doc.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD && !doc.isUpdated)
    return criminalRecords.map(record => {
        const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, record.entityId)
        return ({
            ...record,
            "Name": rjscOfficer ? fullName(rjscOfficer) : "- - - -",
            "Position": rjscOfficer?.position ?? "- - - -",
            "review": ""
        })
    })
}

export function parseFinancialStatementsForRenewal(applicationItem) {

    return [
        {
            "Have the Financial Statements been submitted?": applicationItem?.isFinancialStatementSubmitted ? "Yes" : "No",
            review: ""
        }
    ]
}

export function parseAIRInformationForRenewal(applicationItem, documents) {
    // const airDocument = documents.filter(doc =>
    //     (doc.documentType === CONST.DOCUMENT_TYPE_ANNUAL_INFORMATION_RETURN && doc.changeType !== "DELETE" &&  !doc.isUpdated))

    // let dateOfUpload = ""

    // if (airDocument && airDocument.length > 0) {
    //     const doc = airDocument[0];
    //     dateOfUpload = humanReadableDate(doc.createdDate);
    //     const documentsToReview = airDocument.map((record) => {
    //         return {
    //             ...record,
    //             review: ""
    //         };
    //     });
    //     return [
    //         {
    //             "Has the AIR been submitted?": applicationItem?.isAIRSubmitted ? "Yes" : "No",
    //             review: ""
    //         },
    //         {
    //             "Date received": dateOfUpload,
    //             review: ""
    //         },
    //         ...documentsToReview
    //     ];
    // }

    return [
        {
            "Has the AIR been submitted?": applicationItem?.isAIRSubmitted ? "Yes" : "No",
            review: ""
        }
    ]
}

export function parseInsuranceInformationForRenewal(applicationItem, documents) {
    const insuranceRecords = documents.filter(doc => doc.documentType === CONST.DOCUMENT_TYPE_INSURANCE_CONTINURATION && !doc.isUpdated)
    const documentsToReview = insuranceRecords.map(record => {
        return ({
            ...record,
            "review": "",
        })
    })
    const {
        insuranceContinuationExpiryDate,
        hasActiveErrorsOmissionsInsurance
    } = applicationItem

    return [
        {
            "Do you have active Errors and Omissions insurance": hasActiveErrorsOmissionsInsurance ? "Yes" : "No",
            "Continuation certificate expiry": humanReadableDate(insuranceContinuationExpiryDate),
            review: null
        },
        ...documentsToReview
    ]
}

export function parseInsuranceInformation(applicationItem, documents) {
    const insuranceRecords = documents.filter(doc => doc.documentType === CONST.DOCUMENT_TYPE_INSURANCE_PROOF && !doc.isUpdated)
    return insuranceRecords.map(record => {
        return ({
            ...record,
            "review": "",
        })
    })
}

/*
function getPreviouslyAddedDocuments(reviewDataCollectionDebt) {
    if (!reviewDataCollectionDebt) {
        return []
    }

    let previouslyAddedDocs = [];

    //Mortgage Brokerage, Administrator and Lender
    const criminalRecordsDocs =
        reviewDataCollectionDebt["criminalRecordsCheckInformationMap"] ||
        reviewDataCollectionDebt["brokerCriminalRecordCheckMap"];
    if (criminalRecordsDocs?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(criminalRecordsDocs);
    }

    const insuranceInformationMap =
        reviewDataCollectionDebt["insuranceInformationMap"];
    if (insuranceInformationMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(insuranceInformationMap);
    }

    const airInformationMap =
        reviewDataCollectionDebt["airInformationMap"];
    if (airInformationMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(airInformationMap);
    }

    //Mortgage Brokerage
    const brokerEducationRequirementMap =
        reviewDataCollectionDebt["brokerEducationRequirementMap"];
    if (brokerEducationRequirementMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(brokerEducationRequirementMap);
    }

    const brokerLicenceHistoryMap =
        reviewDataCollectionDebt["brokerLicenceHistoryMap"];
    if (brokerLicenceHistoryMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(brokerEducationRequirementMap);
    }

    //Collection agency
    const suretyBondDataMap =
        reviewDataCollectionDebt["suretyBondDataMap"];
    if (suretyBondDataMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(suretyBondDataMap);
    }

    const documentsDataMap =
        reviewDataCollectionDebt["documentsDataMap"];
    if (documentsDataMap?.length) {
        previouslyAddedDocs = previouslyAddedDocs.concat(documentsDataMap);
    }

    // const updatedDocumentsMap =
    //     reviewDataCollectionDebt["updatedDocumentsMap"];
    // if (updatedDocumentsMap?.length) {
    //     previouslyAddedDocs = previouslyAddedDocs.concat(updatedDocumentsMap);
    // }

    return previouslyAddedDocs;
}
*/

export function isHavingUpdatedDocuments(applicationItem) {
    
    if (
        applicationItem?.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_CHANGE_APPLICATION ||
        applicationItem?.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_CHANGE_APPLICATION ||
        applicationItem?.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_CHANGE_APPLICATION
    ) {
        return false;
    }
 
    const documents = applicationItem?.documents;
    if (!documents) {
        return false;
    }

    for (const doc of documents) {
        if (doc.isUpdated) {
            return true;
        }
    }

    return false;
}

export function parseUpdatedDocumentsInformation(applicationItem, documents) {
    let updatedDocuments = [];

    if (!documents) {
        return updatedDocuments;
    }

    const { companyRjscDetails } = applicationItem
    const peopleList = companyRjscDetails?.businessData?.peopleList ?? []
    const updatedDocumentsMap = applicationItem.reviewDataCollectionDebt?.updatedDocumentsMap;

    for (const doc of documents) {
        let reviewedDoc = null;
        if(updatedDocumentsMap?.length) {
            reviewedDoc = updatedDocumentsMap.find((d)=>parseInt(d.id) === doc.id)
        }
        if (doc.isUpdated) {
            if (doc.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD) {
                const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, doc.entityId)
                updatedDocuments.push({
                    ...doc,
                    "Name": rjscOfficer ? fullName(rjscOfficer) : "- - - -",
                    "Position": rjscOfficer?.position ?? "- - - -",
                    "review": reviewedDoc ? reviewedDoc.review : ""
                })
            }
            else {
                 updatedDocuments.push({
                    ...doc,
                    review: reviewedDoc ? reviewedDoc.review : ""
                });
            }
        }
    }

    return updatedDocuments;
}

export function getUpdatedDocuemntsWithReviewData(applicationItem, documents, licenceStatus) {
    let reviewData;
    const updatedDocumentsMap = parseUpdatedDocumentsInformation(applicationItem, documents);
    if (applicationItem.reviewDataCollectionDebt && updatedDocumentsMap?.length) {
        if (licenceStatus === CONST.LICENCE_STATUS_ACTIVE) {
            if (applicationItem.reviewDataCollectionDebt) {
                reviewData = {
                    ...applicationItem.reviewDataCollectionDebt,
                    updatedDocumentsMap
                };
            } else {
                const internalNotesMap = [
                    {
                        internalNotes: applicationItem.internalNotes,
                        review: applicationItem.internalNotes?.length > 0 ? "complete" : ""
                    }
                ];

                reviewData = {
                    updatedDocumentsMap,
                    internalNotesMap,
                    decisionMap: [
                        {
                            adminDecision: "",
                            review: ""
                        }
                    ]
                };
            }
        } else {
            reviewData = { ...applicationItem.reviewDataCollectionDebt, updatedDocumentsMap };
        }
    } else if (updatedDocumentsMap?.length) {
        const internalNotesMap = [
            {
                internalNotes: applicationItem.internalNotes,
                review: applicationItem.internalNotes?.length > 0 ? "complete" : ""
            }
        ];
        reviewData = {
            updatedDocumentsMap,
            internalNotesMap,
            decisionMap: [
                {
                    adminDecision: "",
                    review: ""
                }
            ]
        };
    }

    if (!reviewData) {
        return applicationItem.reviewDataCollectionDebt;
    }

    return reviewData;
}

/*
export function parseNotAddedDocumentsInformation(applicationItem, documentsContainingMaps, documents) {
    let updatedDocuments = [];

    if (!documentsContainingMaps || !documents) {
        return []
    }

    const previouslyAddedDocs = getPreviouslyAddedDocuments(documentsContainingMaps);

    // Filter documents which were not added previously
    documents?.forEach((doc1) => {
        if (doc1.isUpdated) {
            let foundDoc = previouslyAddedDocs.find((doc2) => {
                return parseInt(doc2.id) === doc1.id
            });

            if (!foundDoc) {
                updatedDocuments.push(doc1);
            }
        }

    });

    const { companyRjscDetails } = applicationItem
    const peopleList = companyRjscDetails?.businessData?.peopleList ?? []

    return updatedDocuments.map((record) => {
        if (record.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD) {
            const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, record.entityId)
            return ({
                ...record,
                "Name": rjscOfficer ? fullName(rjscOfficer) : "- - - -",
                "Position": rjscOfficer?.position ?? "- - - -",
                "review": record.review || ""
            })
        }

        return ({
            ...record,
            review: record.review || ""
        });
    });
}
*/



/*
export function isHavingUpdatedDocuments(applicationItem) {
const documents = applicationItem?.documents;
const reviewDataCollectionDebt = applicationItem?.businessLicenceData?.reviewDataCollectionDebt;

if (!documents?.length || !reviewDataCollectionDebt) {
    return false
}

if(reviewDataCollectionDebt?.updatedDocumentsMap?.length) {
    return true; // Previously added documents needed to show
}

const previouslyAddedDocs = getPreviouslyAddedDocuments(reviewDataCollectionDebt);

for (const doc1 of documents) {
    const foundDoc = previouslyAddedDocs.find((doc2) => { 
        return parseInt(doc2.id) === doc1.id
    });

    if (!foundDoc) {
        return true; //Updated doucment found - true
    }
}

return false;
}
*/


/*
export function parseUpdatedDocumentsInformation(applicationItem, documents) {
    let updatedDocuments = [];

    if (applicationItem?.reviewDataCollectionDebt) {
        const updatedDocumentsMap = applicationItem.reviewDataCollectionDebt.updatedDocumentsMap;
        if(updatedDocumentsMap?.length) {
            updatedDocuments = [...updatedDocuments, ...updatedDocumentsMap]
        }
         
        const previouslyAddedDocs = getPreviouslyAddedDocuments(applicationItem.reviewDataCollectionDebt);

        // Filter documents which were not added previously
        documents?.forEach((doc1) => {
            let foundDoc = previouslyAddedDocs.find((doc2) => {
                return parseInt(doc2.id) === doc1.id
            });

            if (!foundDoc && updatedDocumentsMap?.length) {
                foundDoc = updatedDocumentsMap.find((doc2) => {
                    return parseInt(doc2.id) === doc1.id
                });
            }

            if (!foundDoc) {
                updatedDocuments.push(doc1);
            }
        });

        const { companyRjscDetails } = applicationItem
        const peopleList = companyRjscDetails?.businessData?.peopleList ?? []

        return updatedDocuments.map((record) => {
            if (record.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD) {
                const rjscOfficer = rjscOfficerObjFromEntityId(peopleList, record.entityId)
                return ({
                    ...record,
                    "Name": rjscOfficer ? fullName(rjscOfficer) : "- - - -",
                    "Position": rjscOfficer?.position ?? "- - - -",
                    "review": record.review || ""
                })
            }
            
            return ({
                ...record,
                review: record.review || ""
            });
        });
    }

    return updatedDocuments;
}
*/


export function parseCompanyTypeInformationForRenewal(applicationItem, archiveApplicationItem) {
    const typeStringMapping = {
        corporated: "Corporation",
        partnership: "Partnership",
        solo: "Sole Proprietor"
    }

    return [{
        parameterName: "companyType",
        changeFrom: archiveApplicationItem.companyType ? typeStringMapping[archiveApplicationItem.companyType] : "",
        changeTo: applicationItem.companyType ? typeStringMapping[applicationItem.companyType] : "",
        review: null,
        forceSelection: true
    }]
}

export function parseMortgageBrokerageChangeRequestInformation(applicationItem) {
    const changeRequestDetails = [];

    if (applicationItem) {
        const { changeRequests, companyRjscDetails } = applicationItem
        const { legalName } = decideLegalAndOperatingNames(companyRjscDetails);

        const changeRequestTypeString = (changeRequest) => {
            if (changeRequest.changeRequestType === CHANGE_REQUEST_TYPE_MAIN_AGENT) {
                return "Principal Broker change request"
            }
        }

        changeRequests.forEach((changeRequest) => {
            if (changeRequest.changeRequestStatus === CHANGE_REQUEST_STATUS_SUBMITTED) {
                const { changeRequestData } = changeRequest;

                changeRequestDetails.push({
                    "Change request type": changeRequestTypeString(changeRequest),
                    "review": null,
                }, {
                    'Current Principal Broker': changeRequestData.isCurrentlyActiveAgent ?
                        `Yes, I am the current Principal Broker for ${legalName}.`
                        : `No, I am not the current Principal Broker for ${legalName}.`,
                    review: ""
                }, {
                    'Effective Date': humanReadableDate(changeRequestData.effectiveDate),
                    review: "",
                }, {
                    'Reason for the request': changeRequestData.reasonForChange,
                    review: "",
                })
            }
        })
    }

    return changeRequestDetails;
}

export function parseMortgageBrokerageChangeRequestAgentEmail(applicationItem) {
    const changeRequestDetails = [];

    if (applicationItem) {
        const { changeRequests } = applicationItem

        changeRequests.forEach((changeRequest) => {
            if (changeRequest.changeRequestStatus === CHANGE_REQUEST_STATUS_SUBMITTED) {
                const { changeRequestData } = changeRequest;

                changeRequestDetails.push({
                    'New Principal Broker email': changeRequestData.nextMainAgentEmail,
                    review: '',
                })
            }
        })
    }

    return changeRequestDetails;
}


export function parseLenderOrAdministratorChangeRequestInformation(applicationItem) {
    const changeRequestDetails = [];

    if (applicationItem) {
        const { changeRequests, companyRjscDetails } = applicationItem
        const { legalName } = decideLegalAndOperatingNames(companyRjscDetails);

        const changeRequestTypeString = (changeRequest) => {
            if (changeRequest.changeRequestType === CHANGE_REQUEST_TYPE_MAIN_AGENT) {
                return "Compliance Officer change request"
            }
        }

        changeRequests.forEach((changeRequest) => {
            if (changeRequest.changeRequestStatus === CHANGE_REQUEST_STATUS_SUBMITTED) {
                const { changeRequestData } = changeRequest;

                changeRequestDetails.push({
                    "Change request type": changeRequestTypeString(changeRequest),
                    "review": null,
                }, {
                    'Current Compliance Officer': changeRequestData.isCurrentlyActiveAgent ?
                        `Yes, I am the current Compliance Officer for ${legalName}.`
                        : `No, I am not the current Compliance Officer for ${legalName}.`,
                    review: ""
                }, {
                    'Effective Date': humanReadableDate(changeRequestData.effectiveDate),
                    review: "",
                }, {
                    'Reason for the request': changeRequestData.reasonForChange,
                    review: "",
                })
            }
        })
    }

    return changeRequestDetails;
}

export function parseComplainceOfficerChangeRequestAgentEmail(applicationItem) {
    const changeRequestDetails = [];

    if (applicationItem) {
        const { changeRequests } = applicationItem

        changeRequests.forEach((changeRequest) => {
            if (changeRequest.changeRequestStatus === CHANGE_REQUEST_STATUS_SUBMITTED) {
                const { changeRequestData } = changeRequest;

                changeRequestDetails.push({
                    'New Compliance Officer email': changeRequestData.nextMainAgentEmail,
                    review: '',
                })
            }
        })
    }

    return changeRequestDetails;
}
